/*
 * Copyright Mimic Networks, Inc. 2026.
 */
import { SwitchChangeEventHandler } from 'antd/es/switch';
import { useEffect, useState } from 'react';

import { Node, OperationalStateChangeResp } from '@/client';
import { ConnectivityStateBadge } from '@/components/States/ConnectivityStateBadge';
import { useMessage } from '@/hooks/message';
import { useUpdateNodeOperationalStateMutation } from '@/hooks/mutation/useUpdateNodeOperationalStateMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useSubscribeToEvents } from '@/hooks/useSubscribeToEvents';
import { Flex } from '@/primitives/Flex';
import { Switch } from '@/primitives/Switch';
import { Text } from '@/primitives/Text';
import { Tooltip } from '@/primitives/Tooltip';
import { tokens } from '@/theme';

export type NodeStateProps = {
  isLoading: boolean;
  node: Node;
};

function getOperationalState(node: Node): boolean {
  if (node.hasRunningJob) {
    return node.requestedRevisionState?.operationalState === 'active';
  }
  return node.appliedRevisionState?.operationalState === 'active';
}

export function NodeState({ isLoading, node }: NodeStateProps) {
  const { t } = useMimicTranslation('node');
  const [isOperational, setIsOperational] = useState(() => getOperationalState(node));
  const [message] = useMessage();
  const canManageNodes = useAuthorization('admin');
  const interactionsBlocked = node.requestedRevisionState?.status === 'pending';
  const { mutate } = useUpdateNodeOperationalStateMutation({
    onSuccess: () => {
      message.success(t('operationalStateChangeRequested'));
    },
    onError: () => {
      message.error(t('operationalStateChangeFailed'));
    },
  });

  useEffect(() => {
    setIsOperational(getOperationalState(node));
  }, [node]);

  useSubscribeToEvents<OperationalStateChangeResp>('node:operational-state-changed', (event) => {
    if (!event.entityIds.includes(node.id) || !event.data?.data) return;
    const nextIsOperationalState = event.data.data.operationalState === 'active';
    if (nextIsOperationalState === isOperational) return;
    setIsOperational(nextIsOperationalState);
  });

  const onChange: SwitchChangeEventHandler = (checked) => {
    const state = checked ? 'active' : 'idle';

    mutate({
      nodeId: node.id,
      updateOperationalState: { requestedOperationalState: state },
    });
  };

  if (isLoading) return null;

  return (
    <Flex align="center" gap="md" wrap>
      {!node.dateArchived && (
        <Flex align="center" gap="sm">
          <Text size="md">{t('operationalStateLabel')}</Text>
          <Switch
            checked={isOperational}
            disabled={!canManageNodes || node.hasRunningJob || interactionsBlocked}
            checkedChildren={t('active')}
            unCheckedChildren={<Text type="subtle">{t('idle')}</Text>}
            onChange={onChange}
            dataTestId="operational-state-switch"
          />
        </Flex>
      )}
      <div style={{ width: 1, height: 20, backgroundColor: tokens.color.border.borderDefault }} />
      <Tooltip title={t('nodeConnectivity')}>
        <span>
          <ConnectivityStateBadge state={node.connectivityState} size="md" />
        </span>
      </Tooltip>
    </Flex>
  );
}
